import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Person,
  Business,
  Edit,
  Save,
  Cancel,
  Settings,
  AdminPanelSettings,
  Star,
  Assessment,
  Refresh,
  ViewModule,
  Description,
  PlayArrow,
} from "@mui/icons-material";
import { useAuth } from "../context/EnterpriseAuthContext";
import { useUsage } from "../context/UsageContext";
import { supabase } from "../lib/supabase";
import SettingsDialog from "./SettingsDialog";
import { PageContainer, PageHeader, SectionCard } from "./pagekit";

function Profile() {
  const { state: authState, actions: authActions } = useAuth();
  const { 
    usageData, 
    loading: usageLoading, 
    error: usageError, 
    refreshUsage,
    getUsageColor,
    formatLimit 
  } = useUsage();
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (authState.user?.username) {
      setNewUsername(authState.user.username);
    }
  }, [authState.user?.username]);

  const handleUsernameEdit = () => {
    setEditingUsername(true);
    setError("");
    setSuccess("");
  };

  const handleUsernameSave = async () => {
    if (!newUsername.trim()) {
      setError("Username cannot be empty");
      return;
    }

    if (newUsername.trim() === authState.user?.username) {
      setEditingUsername(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Update the profiles table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ username: newUsername.trim() })
        .eq("id", authState.user.id);

      if (profileError) throw profileError;

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { username: newUsername.trim() },
      });

      if (authError) throw authError;

      // Refresh the account data to update the context
      await authActions.refreshAccountData();

      setSuccess("Username updated successfully!");
      setEditingUsername(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating username:", err);
      setError(err.message || "Failed to update username");
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameCancel = () => {
    setNewUsername(authState.user?.username || "");
    setEditingUsername(false);
    setError("");
  };

  const getUserInitials = (username) => {
    if (!username) return "U";
    return username.charAt(0).toUpperCase();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "account_admin":
        return "primary";
      case "template_creator":
        return "info";
      case "member":
        return "default";
      default:
        return "default";
    }
  };

  const getTierName = () => {
    if (authState.currentMembership?.plan_name) {
      return authState.currentMembership.plan_name;
    }
    // Fallback based on role for legacy users
    if (authActions.isAccountAdmin()) {
      return "Business";
    }
    return "Standard";
  };

  if (!authState.user) {
    return (
      <PageContainer width="narrow">
        <Alert severity="error">Please sign in to view your profile.</Alert>
      </PageContainer>
    );
  }

  const roleLabel = (authState.currentMembership?.role || "member").replace(/_/g, " ");
  const usageItems = usageData
    ? [
        { key: "templates", label: "Templates created", icon: <ViewModule fontSize="small" />, data: usageData.usage.templates },
        { key: "sessions", label: "Sessions this month", icon: <PlayArrow fontSize="small" />, data: usageData.usage.sessions },
        { key: "documents", label: "Documents this month", icon: <Description fontSize="small" />, data: usageData.usage.documents },
      ]
    : [];
  const nearLimit = usageItems.some((u) => u.data.percentage >= 80);
  const atLimit = usageItems.some((u) => u.data.current >= u.data.limit);

  return (
    <PageContainer>
      <PageHeader
        leading={
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48, fontSize: "1.25rem" }}>
            {getUserInitials(authState.user.username)}
          </Avatar>
        }
        title={authState.user.username || "My profile"}
        description={authState.user.email}
        meta={
          <>
            <Chip
              size="small"
              label={roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)}
              color={getRoleColor(authState.currentMembership?.role)}
              icon={authActions.isAccountAdmin() ? <AdminPanelSettings /> : <Person />}
            />
            <Chip size="small" label={`${getTierName()} plan`} icon={<Star />} variant="outlined" />
          </>
        }
        actions={
          authActions.canAccessSettings() ? (
            <Button variant="outlined" startIcon={<Settings />} onClick={() => setSettingsOpen(true)}>
              Settings
            </Button>
          ) : null
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
        <SectionCard title="Your details" icon={<Person />}>
          <Box component="dl" sx={{ m: 0, display: "grid", gap: 2 }}>
            <Box>
              <Typography component="dt" variant="body2" color="text.secondary" gutterBottom>
                Username
              </Typography>
              <Box component="dd" sx={{ m: 0 }}>
                {editingUsername ? (
                  <Box
                    component="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUsernameSave();
                    }}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TextField
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      fullWidth
                      disabled={loading}
                      autoFocus
                      inputProps={{ "aria-label": "Username" }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") handleUsernameCancel();
                      }}
                    />
                    <IconButton type="submit" disabled={loading} color="primary" aria-label="Save username">
                      {loading ? <CircularProgress size={18} /> : <Save fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={handleUsernameCancel} disabled={loading} aria-label="Cancel editing">
                      <Cancel fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1">{authState.user.username}</Typography>
                    <IconButton onClick={handleUsernameEdit} size="small" aria-label="Edit username">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
            <Box>
              <Typography component="dt" variant="body2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <Typography component="dd" variant="body1" sx={{ m: 0, overflowWrap: "anywhere" }}>
                {authState.user.email}
              </Typography>
            </Box>
            <Box>
              <Typography component="dt" variant="body2" color="text.secondary" gutterBottom>
                Plan
              </Typography>
              <Typography component="dd" variant="body1" sx={{ m: 0 }}>
                {getTierName()}
              </Typography>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Organization" icon={<Business />}>
          {authState.activeAccount ? (
            <Box component="dl" sx={{ m: 0, display: "grid", gap: 2 }}>
              {[
                ["Name", authState.activeAccount.name],
                ["Domain", authState.activeAccount.domain || "Not specified"],
                authState.activeAccount.description ? ["Description", authState.activeAccount.description] : null,
                [
                  "Member since",
                  authState.currentMembership?.created_at
                    ? new Date(authState.currentMembership.created_at).toLocaleDateString()
                    : "Unknown",
                ],
              ]
                .filter(Boolean)
                .map(([label, value]) => (
                  <Box key={label}>
                    <Typography component="dt" variant="body2" color="text.secondary" gutterBottom>
                      {label}
                    </Typography>
                    <Typography component="dd" variant="body1" sx={{ m: 0, overflowWrap: "anywhere" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No organization information available.
            </Typography>
          )}
        </SectionCard>
      </Box>

      <SectionCard
        title="Usage and limits"
        description={usageData ? `Your usage on the ${usageData.tierName} plan.` : undefined}
        icon={<Assessment />}
        actions={
          <Tooltip title="Refresh usage">
            <span>
              <IconButton onClick={refreshUsage} disabled={usageLoading} size="small" aria-label="Refresh usage">
                {usageLoading ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        {usageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {usageError}
          </Alert>
        )}

        {usageData ? (
          <>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
              {usageItems.map((item) => {
                const color = getUsageColor(item.key);
                const unlimited = item.data.limit === Infinity;
                return (
                  <Box key={item.key} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: "var(--q-radius-xl)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 1 }}>
                      {item.icon}
                      <Typography variant="body2">{item.label}</Typography>
                    </Box>
                    <Typography variant="h5" component="div" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {item.data.current}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {" "}/ {formatLimit(item.data.limit)}
                      </Typography>
                    </Typography>
                    {!unlimited && (
                      <>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, item.data.percentage || 0)}
                          color={["primary", "secondary", "error", "info", "success", "warning"].includes(color) ? color : "primary"}
                          aria-label={`${item.label}: ${item.data.percentage}% used`}
                          sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {item.data.percentage}% used
                        </Typography>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>

            {atLimit ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                You have reached one or more plan limits. Upgrade to keep using every feature.
              </Alert>
            ) : nearLimit ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You are close to your plan limits. Consider upgrading to avoid interruptions.
              </Alert>
            ) : null}
          </>
        ) : usageLoading ? (
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }} aria-busy="true" aria-label="Loading usage">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={112} sx={{ borderRadius: "var(--q-radius-xl)" }} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
            Unable to load usage data.
          </Typography>
        )}
      </SectionCard>

      <SectionCard
        title="Settings"
        icon={<Settings />}
        description={
          authActions.canAccessSettings()
            ? "Manage AI settings, integrations and billing for your organization."
            : "Settings are managed by your account administrator. You inherit your organization's AI settings."
        }
        actions={
          authActions.canAccessSettings() ? (
            <Button variant="outlined" size="small" startIcon={<Settings />} onClick={() => setSettingsOpen(true)}>
              Open settings
            </Button>
          ) : null
        }
      />

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </PageContainer>
  );
}

export default Profile;
